import {
  type Context,
  createContext,
  useContext,
  useMemo,
  useRef,
} from "react";
import { useStore } from "zustand";

import {
  type StoreCreatorWithMiddlewares,
  createWithMiddlewares,
} from "./zustand-with-middlewares";

type Store<State> = ReturnType<typeof createWithMiddlewares<State>>;

type StoreContext<State> = Context<Store<State> | null> & {
  /** Provider に注入する store を生成する */
  createStore: (initialState?: State) => Store<State>;
};

/**
 * Zustand を ReactContext 化するために必要な書式のユーティリティ
 * @see https://zustand.docs.pmnd.rs/guides/initialize-state-with-props
 */
export function createZustandContext<State extends object>(
  storeCreator: StoreCreatorWithMiddlewares<State>,
  devtoolsOptions?: {
    name?: string;
    enabled?: boolean;
  },
) {
  /**
   * Zustand Store を保有するコンテキスト
   * Provider を活用する際に createStore() で Store を生成することができる
   */
  const context = createContext<Store<State> | null>(null);
  // oxlint-disable react-hooks/rules-of-hooks -- createStoreはコンポーネント内で呼ばれる前提の意図的なパターン
  const createStore = (initialState?: State) =>
    useRef(createWithMiddlewares(storeCreator, initialState, devtoolsOptions))
      .current;
  // oxlint-enable react-hooks/rules-of-hooks
  Object.assign(context, { createStore });

  /**
   * Consumer が活用する useStore のテンプレート。
   * 基本的には、これに名前をつけて export して使えば OK。
   */
  function useZustandStore<T = State>(selector?: (state: State) => T): T;
  function useZustandStore<T = State>(
    selector: (state: State) => T,
    opts: {
      allowNoContext: true; // Context が存在しない場合も throw せずに処理を継続する
    },
  ): T | null;
  function useZustandStore<T = State>(
    selector: (state: State) => T = (state) => state as unknown as T,
    opts?: { allowNoContext?: boolean },
  ): T | null {
    const store = useContext(context);

    if (!store && !opts?.allowNoContext) {
      throw new Error("Missing StoreContext.Provider in the tree");
    }

    // Context のない状態を許容する場合に返す dummy state
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- devtoolsOptionsはcreateZustandContext呼び出し時に固定され変更されないため意図的に省略
    const dummyStore = useMemo(
      () =>
        createWithMiddlewares(
          () => null as unknown as State,
          undefined,
          devtoolsOptions,
        ),
      [],
    );
    return useStore(store ?? dummyStore, store ? selector : () => null);
  }

  return {
    context: context as StoreContext<State>,
    useStore: useZustandStore,
  };
}
